<template>
  <div
    v-if="!badgeAdded"
    id="badgeDiv">
    <v-row>
      <v-col
        cols=7
        class="pt-0">
        <v-autocomplete
          v-if="badges.length || loadingBadges"
          id="badgeAutocomplete"
          v-model="selectedBadge"
          label="Select Badge"
          :items="badges"
          item-text="name"
          item-value="id"
          :disabled="isUpdating"
          :color="playlistColor"
          :loading="loadingBadges"
          chips
          no-data-text="You don't have any badges.  Create one!"
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              close
              @click:close="unselectBadge()"
            >
              <v-avatar
                tile
                left>
                <v-img
                  :src="data.item.image_url"></v-img>
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <v-list-item-avatar
              tile>
              <img :src="data.item.image_url">
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        <v-alert
          v-else-if="credlyLoaded"
          id="noBadgesAlert"
          dense
          type="warning">
          Your Credly account doesn't have any active badges.  You'll need to create one in Credly first.
        </v-alert>
      </v-col>
      <v-col>
        <v-btn
          id="addBadgeButton"
          v-if="selectedBadge"
          dark
          :color="playlistColor"
          @click="handleAddBadge"
          :loading="isUpdating">
          Add badge
        </v-btn>
      </v-col>
    </v-row>
  </div>
  <div
    v-else-if="credlyLoaded"
    id="badgeRow">
    <v-row>
      <v-col>
        <v-list>
          <v-list-item
            class="outlinedListItem">
            <v-list-item-avatar>
              <v-img
                :src="badgeAdded.image_url"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                class="text-left">
                <a :href="badgeAdded.url" target="_blank">{{ badgeAdded.name }}</a>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                icon
                @click="removeBadge()">
                <v-icon color="grey lighten-1">mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import _ from 'lodash';

export default {
  name: 'CredlySettings',
  data: () => ({
    credentialBadge: false,
    badges: [],
    issuerProfile: null,
    loadingBadges: false,
    microcredentialKey: 1,
    microcredentialProviders: ['None'],
    playlistColor: null,
    schoolIssuersLoaded: false,
    schoolIssuersLoading: false,
    selectedBadge: null,
  }),
  computed: {
    ...mapState([
      'courseHash',
      'credlyLoaded',
      'isLoaded',
      'isLti',
      'isUpdating',
      'microcredentialService',
      'playlist',
      'sectionId',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    badgeAdded() {
      return _.get(this.playlist, 'badge.credly.badge', false);
    },
  },
  methods: {
    ...mapMutations([
      'setCredlyLoaded',
      'setIsUpdating',
      'setPlaylist',
      'setSlideout',
      'toggleSnackbar',
    ]),
    ...mapActions([
      'getInitialCredlyData',
    ]),
    async handleAddBadge() {
      try {
        this.setIsUpdating(true);
        const { data: updatedPlaylist } = await this.$api.playlist.addCredlyBadge({
          playlistId: this.playlist.id,
          badge: _.find(this.badges, { id: this.selectedBadge }),
          badgeService: this.microcredentialService,
        });
        this.setPlaylist(updatedPlaylist);
        this.setCredlyLoaded(true);
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error adding badge.',
          color: 'error',
          timeout: 5000,
        });
      } finally {
        this.microcredentialKey += 1;
        this.setIsUpdating(false);
      }
    },
    async handleUpdateBadgeEnabled() {
      try {
        // If there is a badge added to playlist and instructor selects 'None' for badge service - deactivate badge
        if (
          this.badgrLoaded
        ) {
          this.setIsUpdating(true);
          await this.$api.playlist.disableBadge({
            enableBadge: this.microcredentialService !== 'None',
            playlistId: this.playlist.id,
          });
        }
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error deactivating badge.',
          color: 'error',
          timeout: 2500,
        });
      } finally {
        this.setIsUpdating(false);
      }
    },
    handleOpenBadgeLink() {
      window.open(`${this.badgeAdded.openBadgeId}`, '_blank');
    },
    async removeBadge() {
      this.setIsUpdating(true);
      const { data: updatedPlaylist } = await this.$api.playlist.removeBadge({
        badgeEntityId: this.badgeAdded.entityId,
        badgeService: this.microcredentialService,
        playlistId: this.playlist.id,
      });
      this.setPlaylist(updatedPlaylist);
      this.selectedBadge = null;
      this.setIsUpdating(false);
    },
    unselectBadge() {
      this.selectedBadge = null;
    },
  },
  async mounted() {
    this.playlistColor = _.get(this, 'playlist.default_color') ? this.playlist.default_color : '#2196F3';
    try {
      this.loadingBadges = true;
      const { data: initialCredlyData } = await this.getInitialCredlyData();
      this.badges = initialCredlyData;
    } catch(e) {
      this.toggleSnackbar({
        text: 'Error getting badges.',
        color: 'error',
        timeout: 5000,
      });
    } finally {
      this.loadingBadges = false;
    }
  },
};
</script>

<style scoped>
  .outlinedListItem {
    border: 1px solid #ccc;
    border-radius: 16px;
  }
</style>
